import { calculateProjectTotals } from './cost-math';
import { buildCostConfig, mapScopeToInputs } from './cost-math-adapter';
import {
    canonicalRatesheetAuthority,
    ratesheetFingerprint,
} from './ratesheet-fingerprint';
import { validatedMarketRate } from './pricing-match-applier';
import {
    hasExplicitPriceWorkingDriver,
    priceWorkingBillableQuantity,
    priceWorkingRateField,
    priceWorkingUnit,
} from '../utils/price-working-edit';
import { isPerDayPricingUnit } from '../utils/pricing-unit-semantics';
import { isFastTrackManualScope } from './project-workflow';
import { isInternalPricingKey } from '../quote-engine/client-quote-boundary';
import {
    cloneCanonicalJson,
    hashCanonicalJson,
    sha256Text,
} from '../quote-engine/canonical-json';
import type { JsonObject, JsonValue, Sha256Digest } from '../quote-engine/contracts';
import {
    createQuoteMakerCostMathPricingAuthorityV1,
} from '../quote-engine/quotemaker-production-services';
import {
    findInternalStaffArtifactLeakPath,
    projectClientQuoteStructuredV1,
    projectClientQuoteTextV1,
    projectStaffCostingStructuredV1,
    projectStaffCostingTextV1,
    projectStaffDiagnosticTextV1,
    projectStaffMethodTextV1,
    type ClientQuoteStructuredV1,
    type QuoteArtifactContentHashesV1,
    type StaffCostingStructuredV1,
} from '../quote-engine/quote-artifact-package';
import {
    isObjectivePreambleVoice,
    isUserOwnedScopeCopy,
    resolveClientScopeDescription,
} from '../utils/scope-descriptions';
import { hasReviewableManualAmountAuthority } from '../utils/quote-pricing-readiness';
import {
    pricingRowHasPendingResolution,
    scopeItemHasUnresolvedPricing,
} from './pricing-resolution';

const KIND = 'quotemaker.production-quote-artifacts' as const;
const AUTHORITY_ID = 'quotemaker-current-business-pricing-v1';
const INTERNAL_METHOD_KEYS = new Set([
    'pricingbreakdown',
    'pricingledger',
    'costbreakdown',
    'costbreakdownlocked',
    'costsummary',
    'costing',
    'costings',
    'internalpricing',
    'estimatedamount',
    'amount',
    'amountexgst',
    'amountincgst',
    'cost',
    'rate',
    'unitrate',
    'total',
    'subtotal',
    'margin',
    'markup',
    'overhead',
    'contingency',
    'needspricing',
]);
const MEDIA_KEYS = new Set([
    'blob', 'blobid', 'bytes', 'data', 'dataurl', 'file', 'heroimage', 'image',
    'images', 'rawpayload', 'thumbnail', 'url', 'workerkeyframes',
]);
const METHOD_TOP_LEVEL_KEYS = [
    'version', 'document_title', 'documentTitle', 'project', 'business', 'overview',
    'activities', 'programme', 'applicable_standards', 'project_risks_to_method',
    'responsibilities', 'sign_off', 'footer',
] as const;
const METHOD_ACTIVITY_KEYS = [
    'id', 'scopeItemRef', 'scope_item_ref', 'title', 'description', 'why_this_method',
    'is_recommendation', 'notes_to_client', 'sequence_of_works', 'trades_required',
    'materials_required', 'equipment_required', 'access_strategy', 'programme_days',
    'standards_applied', 'risks_to_method', 'quality_checkpoints', 'notes_to_contractor',
    'hold_points', 'applied_learnings_cited', 'risk_refs',
] as const;

export interface CurrentPricingAuthorityContextV1 extends JsonObject {
    schemaVersion: 1;
    authorityId: string;
    authorityRefs: string[];
    ratesheetFingerprint: string;
    ratesheetSnapshot: JsonObject;
    ratesheetSnapshotHash: Sha256Digest;
}

export interface ProductionTextArtifactV1<TStructured extends JsonObject = JsonObject> {
    structured: TStructured;
    text: string;
    hashes: QuoteArtifactContentHashesV1;
}

export interface ProductionQuoteArtifactsV1 {
    schemaVersion: 1;
    kind: typeof KIND;
    generatedAt: string;
    projectId: string | null;
    quoteBinding: JsonObject;
    staffDiagnosticReport: ProductionTextArtifactV1;
    staffMethodReport: ProductionTextArtifactV1;
    staffCosting: ProductionTextArtifactV1<StaffCostingStructuredV1>;
    clientQuote: ProductionTextArtifactV1<ClientQuoteStructuredV1>;
    integrity: JsonObject;
}

export interface BuildProductionQuoteArtifactsInputV1 {
    projectId?: string | null;
    quoteData: Record<string, unknown>;
    analysis?: unknown;
    diagnosticResult?: unknown;
    projectWMS?: unknown;
    /** True only when the supplied WMS is bound to the scope being quoted. */
    projectWMSIsCurrent?: boolean;
    workflowMode?: string | null;
    formData?: unknown;
    pricingAuthority: CurrentPricingAuthorityContextV1;
    projectDefaultMargin?: number;
    signal?: AbortSignal;
}

type UnknownRecord = Record<string, unknown>;

const COMMERCIAL_CATEGORY_KEYS = [
    'materials', 'labour', 'equipment', 'subcontractors',
] as const;
const RATEBOOK_CATEGORY_BY_COST_CATEGORY: Record<(typeof COMMERCIAL_CATEGORY_KEYS)[number], string> = {
    materials: 'material_costs',
    labour: 'labour_rates',
    equipment: 'equipment',
    subcontractors: 'subcontractors',
};

function fail(code: string, message: string): never {
    const error = new Error(message) as Error & { code: string };
    error.code = code;
    throw error;
}

function normaliseKey(key: string): string {
    return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function isRecord(value: unknown): value is UnknownRecord {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

function portableValue(value: unknown, path = '$'): JsonValue {
    let serialised: string | undefined;
    try {
        serialised = JSON.stringify(value, (_key, entry) => {
            if (typeof entry === 'function' || typeof entry === 'undefined') return undefined;
            if (typeof entry === 'bigint') return String(entry);
            return entry;
        });
    } catch (error) {
        fail('QUOTE_ARTIFACT_NON_PORTABLE', `${path} is not portable JSON: ${(error as Error)?.message || error}`);
    }
    if (serialised === undefined) return null;
    return JSON.parse(serialised) as JsonValue;
}

function portableObject(value: unknown, path: string): JsonObject {
    const portable = portableValue(value, path);
    if (!portable || typeof portable !== 'object' || Array.isArray(portable)) {
        fail('QUOTE_ARTIFACT_INVALID_OBJECT', `${path} must be an object.`);
    }
    return portable;
}

function stripStaffPricingAndMedia(value: JsonValue): JsonValue {
    if (Array.isArray(value)) return value.map(stripStaffPricingAndMedia);
    if (!value || typeof value !== 'object') return value;
    const result: JsonObject = {};
    for (const [key, entry] of Object.entries(value)) {
        const normalised = normaliseKey(key);
        if (MEDIA_KEYS.has(normalised)
            || INTERNAL_METHOD_KEYS.has(normalised)
            || isInternalPricingKey(key)) continue;
        result[key] = stripStaffPricingAndMedia(entry);
    }
    return result;
}

function selectPortableFields(
    value: unknown,
    keys: readonly string[],
    path: string,
): JsonObject {
    if (!isRecord(value)) return {};
    const selected: JsonObject = {};
    for (const key of keys) {
        if (value[key] !== undefined) selected[key] = portableValue(value[key], `${path}.${key}`);
    }
    return stripStaffPricingAndMedia(selected) as JsonObject;
}

function projectMethodDocument(value: unknown): JsonObject {
    const source = isRecord(value) ? value : {};
    const selected = selectPortableFields(source, METHOD_TOP_LEVEL_KEYS, '$productionArtifacts.staffMethodReport');
    const activities = Array.isArray(source.activities)
        ? source.activities.filter(isRecord).map((activity, index) => selectPortableFields(
            activity,
            METHOD_ACTIVITY_KEYS,
            `$productionArtifacts.staffMethodReport.activities[${index}]`,
        ))
        : [];
    if (activities.length > 0) selected.activities = activities;
    return selected;
}

function hashes(structured: JsonObject, text: string): QuoteArtifactContentHashesV1 {
    return {
        structuredHash: hashCanonicalJson(structured),
        textHash: sha256Text(text),
    };
}

function textArtifact<TStructured extends JsonObject>(
    structured: TStructured,
    text: string,
): ProductionTextArtifactV1<TStructured> {
    return { structured, text, hashes: hashes(structured, text) };
}

function hasMeaningfulValue(value: unknown): boolean {
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.some(hasMeaningfulValue);
    if (!isRecord(value)) return value !== null && value !== undefined;
    return Object.values(value).some(hasMeaningfulValue);
}

function workflowSkipRecord(
    artifact: 'diagnostic' | 'method',
    workflowMode: string,
): JsonObject {
    const stage = artifact === 'diagnostic' ? 'Stage 2 diagnosis and risk report' : 'Stage 3 Work Method';
    return {
        status: 'not_performed',
        workflowMode,
        reason: `${stage} was not performed because this project uses the Fast-Track manual-scope workflow.`,
    };
}

function curatedRiskReport(value: unknown): JsonObject | null {
    if (!isRecord(value)) return null;
    const selected = selectPortableFields(value, [
        'executive_summary_headline', 'executive_summary', 'executive_summary_condition',
        'executive_summary_findings', 'executive_summary_action', 'detected_risks',
        'detected_standards', 'specific_questions', 'recommended_scope_narrative',
        'assessment_methodology', 'confidence_score', 'overall_quality',
    ], '$productionArtifacts.staffDiagnosticReport.riskReport');
    return hasMeaningfulValue(selected) ? selected : null;
}

function curatedPhotoForensic(value: unknown): JsonObject | null {
    if (!isRecord(value)) return null;
    const selected = selectPortableFields(value, [
        'coverage', 'coverage_status', 'photos_analysed', 'input_photos_count',
        'selected_photos_count', 'submitted_photos_count', 'unreadable_photos',
        'unreadable_photo_count', 'requires_review', 'batch_summary', 'locality',
        'source_fingerprint', 'source_fingerprint_version',
    ], '$productionArtifacts.staffDiagnosticReport.photoForensicResult');
    const observationKeys = [
        'photo_id', 'photo_name', 'location_tag', 'visible_defects',
        'probable_failure_mechanisms', 'safety_concerns', 'uncertainty_flags',
        'confirms_inspector', 'contradicts_inspector', 'applicable_standards_hints',
    ] as const;
    if (Array.isArray(value.observations)) {
        selected.observations = (value.observations as unknown[]).filter(isRecord).map((observation, index) => (
            selectPortableFields(
                observation,
                observationKeys,
                `$productionArtifacts.staffDiagnosticReport.photoForensicResult.observations[${index}]`,
            )
        ));
    }
    return hasMeaningfulValue(selected) ? selected : null;
}

const DELIVERY_CERTAINTY_CLAIM_RE = /(?:structural(?:ly)?\s+(?:section\s+(?:loss|degradation)|collapse|failure|degraded|compromised)|compromis(?:e|es|ed|ing)\s+(?:structural\s+)?(?:integrity|capacity)|mandatory\b|non[- ]compliant|sub[- ]standard|statutory\s+minimum|fails?\s+(?:the\s+)?current|falls?\s+short\s+of|violat(?:e|es|ion)|requires?\s+(?:full|complete)|complete\s+removal|full\s+replacement|load[- ]bearing|certified\s+temporary|weld(?:ed)?\s+(?:gusset|sister)|chemical\s+anchors?|waterproofing\s+failure|ponding\s+water)/i;
const CONDITIONAL_SCOPE_RE = /(?:engineer.{0,80}(?:confirm|inspect|detail|nominate)|repair\s+or\s+replace|only\s+the\s+deteriorated|retain\s+sound|pending\s+(?:review|confirmation)|subject\s+to)/i;

function clientSafePhotoForensic(value: unknown): JsonObject | null {
    if (!isRecord(value)) return null;
    const selected = selectPortableFields(value, [
        'coverage', 'coverage_status', 'photos_analysed', 'input_photos_count',
        'selected_photos_count', 'submitted_photos_count', 'unreadable_photos',
        'unreadable_photo_count', 'source_fingerprint', 'source_fingerprint_version',
    ], '$productionArtifacts.staffDiagnosticReport.scopeAligned.photoForensicResult');
    selected.requires_review = true;
    selected.evidence_policy = 'Observations only; pending professional confirmation';
    if (Array.isArray(value.observations)) {
        selected.observations = (value.observations as unknown[]).filter(isRecord).flatMap((observation, index): JsonValue[] => {
            const visibleDefects = Array.isArray(observation.visible_defects)
                ? observation.visible_defects
                    .map(entry => String(entry || '').trim())
                    .filter(entry => entry && !DELIVERY_CERTAINTY_CLAIM_RE.test(entry))
                : [];
            const safe = selectPortableFields(
                observation,
                [
                    'photo_id', 'photo_name', 'location_tag', 'uncertainty_flags',
                    'confirms_inspector', 'contradicts_inspector',
                ],
                `$productionArtifacts.staffDiagnosticReport.scopeAligned.photoForensicResult.observations[${index}]`,
            );
            if (visibleDefects.length > 0) safe.visible_defects = visibleDefects;
            return hasMeaningfulValue(safe) ? [safe] : [];
        });
    }
    return hasMeaningfulValue(selected) ? selected : null;
}

function scopeText(scope: UnknownRecord[]): string {
    return JSON.stringify(scope.map(item => ({
        title: item.title,
        description: item.description,
        inclusions: item.inclusions,
        exclusions: item.exclusions,
        notes: item.notes,
    })));
}

function sourceDiagnosisConflictsWithQuotedBoundary(
    source: JsonObject,
    quoteScope: UnknownRecord[],
): boolean {
    if (!Array.isArray(quoteScope) || quoteScope.length === 0) return false;
    return CONDITIONAL_SCOPE_RE.test(scopeText(quoteScope))
        && DELIVERY_CERTAINTY_CLAIM_RE.test(JSON.stringify(source));
}

function safeObservedFacts(diagnosis: UnknownRecord): JsonValue[] {
    const facts = Array.isArray(diagnosis.agreed_facts)
        ? diagnosis.agreed_facts.filter(isRecord)
        : [];
    return facts.flatMap((fact, index): JsonValue[] => {
        const finding = String(fact.finding || fact.fact || '').trim();
        const epistemicStatus = String(fact.epistemic_status || fact.epistemicStatus || '').trim();
        if (!finding || DELIVERY_CERTAINTY_CLAIM_RE.test(finding)) return [];
        if (epistemicStatus && !/direct|measured|observed/i.test(epistemicStatus)) return [];
        return [selectPortableFields(
            fact,
            ['finding', 'fact', 'evidence_refs', 'epistemic_status', 'epistemicStatus'],
            `$productionArtifacts.staffDiagnosticReport.scopeAligned.observedFacts[${index}]`,
        )];
    });
}

function safeEvidenceObservations(evidenceResult: unknown): JsonValue[] {
    const evidence = isRecord(evidenceResult) ? evidenceResult : {};
    const rows = Array.isArray(evidence.canonicalEvidence)
        ? evidence.canonicalEvidence.filter(isRecord)
        : (Array.isArray(evidence.canonical_evidence)
            ? evidence.canonical_evidence.filter(isRecord)
            : []);
    return rows.flatMap((row, index): JsonValue[] => {
        const status = String(row.epistemicStatus || row.epistemic_status || '').trim();
        if (!/^(?:measured_observation|direct_observation)$/i.test(status)) return [];
        const observation = String(row.observationText || row.observation_text || '').trim();
        const claims = Array.isArray(row.claimsMade)
            ? row.claimsMade
            : (Array.isArray(row.claims_made) ? row.claims_made : []);
        const combined = `${observation} ${claims.map(String).join(' ')}`;
        if (!observation || DELIVERY_CERTAINTY_CLAIM_RE.test(combined)) return [];
        return [selectPortableFields(
            row,
            [
                'sourceId', 'source_id', 'sourceType', 'source_type',
                'locationTag', 'location_tag', 'observationText', 'observation_text',
                'epistemicStatus', 'epistemic_status', 'contradictionsFlagged',
                'contradictions_flagged',
            ],
            `$productionArtifacts.staffDiagnosticReport.scopeAligned.evidenceObservations[${index}]`,
        )];
    });
}

function scopeAlignedDiagnostic(
    source: JsonObject,
    diagnostic: UnknownRecord,
    quoteScope: UnknownRecord[],
): JsonObject {
    const projectDiagnosis = isRecord(diagnostic.projectDiagnosis) ? diagnostic.projectDiagnosis : {};
    const quotedScope = quoteScope.map((item, index) => selectPortableFields(
        item,
        ['id', 'code', 'title', 'description', 'inclusions', 'exclusions', 'notes', 'note'],
        `$productionArtifacts.staffDiagnosticReport.scopeAligned.quotedScope[${index}]`,
    ));
    const holds = quotedScope.flatMap(item => {
        const candidates = [
            ...(Array.isArray(item.inclusions) ? item.inclusions : []),
            ...(Array.isArray(item.exclusions) ? item.exclusions : []),
            ...(Array.isArray(item.notes) ? item.notes : []),
            ...(typeof item.note === 'string' ? [item.note] : []),
        ];
        return candidates.filter(value => typeof value === 'string' && CONDITIONAL_SCOPE_RE.test(value));
    });
    const disputedFacts = Array.isArray(projectDiagnosis.disputed_facts)
        ? projectDiagnosis.disputed_facts.filter(isRecord).map((fact, index) => selectPortableFields(
            fact,
            ['finding', 'fact', 'question', 'claim', 'reason', 'evidence_refs'],
            `$productionArtifacts.staffDiagnosticReport.scopeAligned.openQuestions[${index}]`,
        ))
        : [];
    const observedFacts = [
        ...safeObservedFacts(projectDiagnosis),
        ...safeEvidenceObservations(diagnostic.evidenceResult ?? diagnostic.evidenceIndex),
    ];
    return stripStaffPricingAndMedia(portableObject({
        diagnosis: {
            title: 'Evidence and quoted remediation basis',
            status: 'scope_aligned_provisional',
            summary: 'The Stage 2 working assessment contained delivery-certainty claims that were not carried into the priced Stage 4 scope. The conditional repair boundary and hold points below are authoritative for this unsent quote.',
            observed_facts: observedFacts,
            open_questions: disputedFacts.length > 0 ? disputedFacts : Array.from(new Set(holds)),
            quoted_remediation_boundary: quotedScope,
            unresolved_design_holds: Array.from(new Set(holds)),
            source_assessment_disposition: {
                status: 'superseded_for_delivery',
                reason: 'The source assessment asserted a fixed repair, replacement, structural or compliance outcome while the priced scope correctly defers that decision to inspection, engineering or another explicit hold point.',
                sourceHash: hashCanonicalJson(source),
            },
        },
        decisionPath: diagnostic.decisionPath,
        decisionReason: diagnostic.decisionReason,
        quoteDiagDisposition: diagnostic.quoteDiagDisposition,
        // The earlier photo model is supporting evidence, not the delivery
        // authority. When its diagnosis was superseded, retain only direct
        // observations and uncertainty; do not publish its failure-mechanism,
        // compliance, standards or structural-capacity conjecture as a staff
        // instruction beside the corrected quote.
        photoForensicResult: clientSafePhotoForensic(diagnostic.photoForensicResult) || undefined,
        warnings: diagnostic.warnings,
    }, '$productionArtifacts.staffDiagnosticReport.scopeAligned')) as JsonObject;
}

function evidencePipelineRequiresReview(value: unknown): boolean {
    if (!isRecord(value)) return false;
    const error = String(value.error || value.canonicalisationError || value.canonicalizationError || '').trim();
    if (error) return true;
    const explicitTotal = Number(value.totalEvidenceItems ?? value.total_evidence_items);
    const canonical = Array.isArray(value.canonicalEvidence)
        ? value.canonicalEvidence
        : (Array.isArray(value.canonical_evidence) ? value.canonical_evidence : null);
    return Number.isFinite(explicitTotal) && explicitTotal === 0 && Array.isArray(canonical) && canonical.length === 0;
}

function evidenceGuardedDiagnostic(
    source: JsonObject,
    diagnostic: UnknownRecord,
    quoteScope: UnknownRecord[],
): JsonObject {
    const quotedScope = quoteScope.map((item, index) => selectPortableFields(
        item,
        ['id', 'code', 'title', 'description', 'inclusions', 'exclusions', 'notes', 'note'],
        `$productionArtifacts.staffDiagnosticReport.evidenceGuarded.quotedScope[${index}]`,
    ));
    return stripStaffPricingAndMedia(portableObject({
        diagnosis: {
            title: 'Evidence and proposed remediation basis',
            status: 'evidence_pipeline_requires_review',
            summary: 'The canonical evidence could not be validated for this run. The direct photo observations and uncertainty flags below are retained, but the source diagnosis is withheld from delivery until a builder checks it against the RFQ, assessor notes and reference documents.',
            quoted_remediation_boundary: quotedScope,
            source_assessment_disposition: {
                status: 'withheld_from_delivery',
                reason: 'The evidence canonicalisation step failed or returned an explicitly empty evidence set, so unverified causal, compliance and fixed-scope claims cannot be repeated as staff instructions.',
                sourceHash: hashCanonicalJson(source),
            },
        },
        decisionPath: diagnostic.decisionPath,
        decisionReason: diagnostic.decisionReason,
        quoteDiagDisposition: diagnostic.quoteDiagDisposition,
        photoForensicResult: clientSafePhotoForensic(diagnostic.photoForensicResult) || undefined,
        warnings: [
            ...(Array.isArray(diagnostic.warnings) ? diagnostic.warnings : []),
            'Builder review required: reconcile the RFQ, assessor notes and referenced documents before issuing the diagnosis or quote.',
        ],
    }, '$productionArtifacts.staffDiagnosticReport.evidenceGuarded')) as JsonObject;
}

function diagnosticStructured(
    analysis: unknown,
    diagnosticResult: unknown,
    workflowMode: string,
    quoteScope: UnknownRecord[] = [],
): JsonObject {
    const diagnostic = isRecord(diagnosticResult) ? diagnosticResult : {};
    const risk = isRecord(analysis) ? analysis : {};
    const riskReport = curatedRiskReport(analysis);
    const riskDerivedDiagnosis = hasMeaningfulValue(risk)
        ? {
            title: risk.executive_summary_headline || 'Evidence-backed defect assessment',
            summary: risk.executive_summary || risk.executive_summary_findings || null,
            condition: risk.executive_summary_condition || null,
            recommendedScope: risk.recommended_scope_narrative || null,
            findings: risk.detected_risks || risk.risks || [],
        }
        : null;
    const candidates: UnknownRecord = {
        diagnosis: diagnostic.projectDiagnosis ?? riskDerivedDiagnosis,
        riskReport: riskReport || undefined,
        decisionPath: diagnostic.decisionPath,
        decisionReason: diagnostic.decisionReason,
        quoteDiagDisposition: diagnostic.quoteDiagDisposition,
        evidenceResult: diagnostic.evidenceResult ?? diagnostic.evidenceIndex,
        photoForensicResult: curatedPhotoForensic(diagnostic.photoForensicResult) || undefined,
        warnings: diagnostic.warnings,
    };
    const selected = portableObject(Object.fromEntries(
        Object.entries(candidates).filter(([, value]) => value !== null && value !== undefined),
    ), '$productionArtifacts.staffDiagnosticReport');
    const safe = stripStaffPricingAndMedia(selected) as JsonObject;
    if (evidencePipelineRequiresReview(diagnostic.evidenceResult ?? diagnostic.evidenceIndex)) {
        return evidenceGuardedDiagnostic(safe, diagnostic, quoteScope);
    }
    if (sourceDiagnosisConflictsWithQuotedBoundary(safe, quoteScope)) {
        return scopeAlignedDiagnostic(safe, diagnostic, quoteScope);
    }
    if (!hasMeaningfulValue(safe.diagnosis) && !hasMeaningfulValue(safe.riskReport)) {
        if (isFastTrackManualScope(workflowMode)) {
            return workflowSkipRecord('diagnostic', workflowMode);
        }
        fail(
            'QUOTE_ARTIFACT_DIAGNOSTIC_MISSING',
            'Stage 5 cannot create its staff diagnostic artifact because no Stage 2 diagnosis or risk report is present.',
        );
    }
    return safe;
}

function methodStructured(
    projectWMS: unknown,
    quoteScope: UnknownRecord[],
    workflowMode: string,
    projectWMSIsCurrent: boolean,
    businessOverride: unknown,
): { structured: JsonObject; source: 'project-wms' | 'quote-scope' | 'workflow-skipped'; sourceHash: Sha256Digest } {
    // Stage 3 is the reasoning surface; Stage 4 is the commercial decision.
    // The issued staff method must therefore be a deterministic projection of
    // the final priced inclusions, not a second competing scope authored by an
    // earlier AI pass. Safe identity and programme metadata may be carried
    // forward, but the work sequence and hold points come from Stage 4.
    if (isFastTrackManualScope(workflowMode) && !projectWMSIsCurrent) {
        const skipped = workflowSkipRecord('method', workflowMode);
        return { structured: skipped, source: 'workflow-skipped', sourceHash: hashCanonicalJson(skipped) };
    }
    const wms = projectWMSIsCurrent && isRecord(projectWMS) ? projectWMS : {};
    const wmsStructured = isRecord(wms.structured) ? wms.structured : wms;
    const safeWms = projectMethodDocument(wmsStructured);
    const business = isRecord(safeWms.business) ? safeWms.business : {};
    const override = isRecord(businessOverride) ? businessOverride : {};
    const identityValue = (primary: unknown, fallback: unknown): string => {
        const value = primary || fallback || '';
        return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
    };
    const hydratedBusiness = {
        ...business,
        name: identityValue(business.name, override.name),
        abn: identityValue(business.abn, override.abn),
        licence: identityValue(business.licence, override.licence),
        contact: identityValue(business.contact, override.contact),
        phone: identityValue(business.phone, override.phone),
        email: identityValue(business.email, override.email),
    };
    const activities = quoteScope.flatMap((item, index): JsonObject[] => {
        const method = isRecord(item.workMethod) ? item.workMethod : {};
        const rawInclusions = Array.isArray(item.inclusions) ? item.inclusions : [];
        const inclusions = rawInclusions.flatMap((entry): string[] => {
            if (typeof entry === 'string' && entry.trim()) return [entry.trim()];
            if (!isRecord(entry)) return [];
            if (/^(?:heading|subheading|section)$/i.test(String(entry.type || '').trim())) return [];
            const text = String(entry.text || entry.description || '').trim();
            return text ? [text] : [];
        }).filter(text => !isObjectivePreambleVoice(text));
        const resolvedDescription = resolveClientScopeDescription(item, { fallbackToTitle: false });
        const description = String(resolvedDescription || item.descriptionBrief || '').trim();
        const sequence = (inclusions.length > 0 ? inclusions : (description ? [description] : []))
            .map((action, sequenceIndex) => ({ order: sequenceIndex + 1, action }));
        if (sequence.length === 0) return [];
        const holds = inclusions.filter(text => CONDITIONAL_SCOPE_RE.test(text));
        const activity: JsonObject = {
            id: String(item.id || item.code || index + 1),
            scopeItemRef: String(item.id || item.code || index + 1),
            title: String(item.title || `Scope item ${index + 1}`),
            ...(description ? { description } : {}),
            sequence_of_works: sequence,
            ...(holds.length > 0 ? { hold_points: Array.from(new Set(holds)) } : {}),
        };
        const programmeDays = finiteCommercialNumber(method.programme_days ?? method.programmeDays);
        if (programmeDays !== null && programmeDays > 0) activity.programme_days = programmeDays;
        const accessStrategy = String(method.access_strategy || method.accessStrategy || '').trim();
        if (accessStrategy && !DELIVERY_CERTAINTY_CLAIM_RE.test(accessStrategy)) {
            activity.access_strategy = accessStrategy;
        }
        return [activity];
    });
    let safe: JsonObject = {
        documentTitle: 'Staff Work Method and Programme',
        ...(hasMeaningfulValue(hydratedBusiness) ? { business: hydratedBusiness } : {}),
        activities,
    };
    let source: 'project-wms' | 'quote-scope' | 'workflow-skipped' = 'quote-scope';
    if (activities.length === 0) {
        if (isFastTrackManualScope(workflowMode)) {
            safe = workflowSkipRecord('method', workflowMode);
            source = 'workflow-skipped';
            return { structured: safe, source, sourceHash: hashCanonicalJson(safe) };
        }
        fail(
            'QUOTE_ARTIFACT_METHOD_MISSING',
            'Stage 5 cannot create its Work Method artifact because the current project has no method activities.',
        );
    }
    return {
        structured: safe,
        source,
        sourceHash: hashCanonicalJson(safe),
    };
}

function clientTextList(value: unknown): JsonValue {
    if (!Array.isArray(value)) return [];
    return value.flatMap((entry): JsonValue[] => {
        if (typeof entry === 'string') return entry.trim() ? [entry.trim()] : [];
        if (!isRecord(entry)) return [];
        const text = String(entry.text || '').trim();
        if (!text) return [];
        const type = String(entry.type || '').trim();
        return [{ ...(type ? { type } : {}), text } as JsonObject];
    });
}

function optionalClientSection(value: unknown): JsonValue | undefined {
    if (typeof value === 'string') return value.trim() || undefined;
    if (Array.isArray(value)) return clientTextList(value);
    if (!isRecord(value)) return undefined;
    const safe = stripStaffPricingAndMedia(portableValue(value));
    return hasMeaningfulValue(safe) ? safe : undefined;
}

function clientQuoteSource(
    quote: UnknownRecord,
    costing: StaffCostingStructuredV1,
    method: JsonObject,
): JsonObject {
    const header = isRecord(quote.header) ? quote.header : {};
    const meta = isRecord(quote.meta) ? quote.meta : {};
    const client = isRecord(quote.client) ? quote.client : {};
    const quoteScope = Array.isArray(quote.scope)
        ? quote.scope.filter(isRecord)
        : [];
    const byId = new Map(quoteScope.map((item, index) => [
        String(item.id || item.code || index + 1),
        item,
    ]));
    const methodActivities: UnknownRecord[] = Array.isArray(method.activities)
        ? (method.activities as unknown[]).filter(isRecord)
        : [];
    const methodByScopeId = new Map(methodActivities.map((activity, index) => [
        String(activity.scopeItemRef || activity.scope_item_ref || activity.id || index + 1),
        activity,
    ]));
    const scope = costing.rows.map((row, index) => {
        const source = byId.get(row.scopeItemId) || quoteScope[index] || {};
        const methodActivity: UnknownRecord = methodByScopeId.get(row.scopeItemId) || methodActivities[index] || {};
        const item: JsonObject = {
            id: row.scopeItemId,
            amount: row.totalIncGstCents / 100,
            title: String(source.title || row.title),
        };
        const titleKey = normaliseKey(String(item.title || ''));
        const resolvedDescription = resolveClientScopeDescription(source, { fallbackToTitle: false });
        const isUserOwned = isUserOwnedScopeCopy(source);
        let clientDescription = resolvedDescription;
        if (!clientDescription && !isUserOwned) {
            const methodDescription = typeof methodActivity.description === 'string'
                ? methodActivity.description.trim()
                : '';
            const rawDescription = typeof source.description === 'string' ? source.description.trim() : '';
            clientDescription = [methodDescription, rawDescription]
                .find(value => value
                    && normaliseKey(value) !== titleKey
                    && !isObjectivePreambleVoice(value)) || '';
        }
        if (clientDescription && normaliseKey(clientDescription) !== titleKey && !isObjectivePreambleVoice(clientDescription)) {
            item.description = clientDescription;
        }
        for (const key of ['code', 'subheading', 'subHeading', 'sectionTitle', 'sectionHeading']) {
            const value = source[key];
            if (typeof value === 'string' && value.trim()) item[key] = value.trim();
        }
        const inclusions = clientTextList(source.inclusions);
        const exclusions = clientTextList(source.exclusions);
        const notes = clientTextList(source.notes);
        if (Array.isArray(inclusions) && inclusions.length > 0) item.inclusions = inclusions;
        if (Array.isArray(exclusions) && exclusions.length > 0) item.exclusions = exclusions;
        if (Array.isArray(notes) && notes.length > 0) item.notes = notes;
        if (typeof source.note === 'string' && source.note.trim()) item.note = source.note.trim();
        return item;
    });
    const source: JsonObject = {
        header: {
            title: String(header.title || '').trim(),
            subtitle: String(header.subtitle || '').trim(),
            projectRef: String(header.projectRef || '').trim(),
        },
        meta: {
            // QuoteMaker's issued quote number and the client's RFQ / order
            // number are different identifiers. The former is allocated in
            // projects.qm_quote_no and hydrated into meta.qmQuoteNo; the
            // legacy meta.quoteNumber field is the request number shown as
            // "Quote Request #" in Stage 5.
            quoteNumber: String(meta.qmQuoteNo || '').trim(),
            quoteRequestNumber: String(meta.quoteRequestNumber || meta.quoteNumber || '').trim(),
            date: String(meta.date || '').trim(),
            validity: String(meta.validity || '').trim(),
        },
        client: {
            name: String(client.name || '').trim(),
            company: String(client.company || '').trim(),
            attention: String(client.attention || '').trim(),
            address: String(client.address || '').trim(),
        },
        scope,
        totals: {
            subtotalExGst: costing.totals.subtotalExGstCents / 100,
            gst: costing.totals.gstCents / 100,
            totalIncGst: costing.totals.totalIncGstCents / 100,
        },
    };
    if (typeof quote.scopeObjective === 'string' && quote.scopeObjective.trim()) {
        source.scopeObjective = quote.scopeObjective.trim();
    }
    for (const key of ['summary', 'standards', 'exclusions', 'insurances', 'notes', 'note', 'terms']) {
        if (key === 'standards' && CONDITIONAL_SCOPE_RE.test(scopeText(quoteScope))) continue;
        const value = optionalClientSection(quote[key]);
        if (value !== undefined) source[key] = value;
    }
    return source;
}

function isStatutoryItem(item: UnknownRecord): boolean {
    return item.gstExempt === true
        || item.gst_exempt === true
        || /hbcf|home\s+building\s+compensation|icare/i.test(
            `${String(item.id || '')} ${String(item.title || '')}`,
        );
}

function sourceLines(item: UnknownRecord): UnknownRecord[] {
    const breakdown = isRecord(item.costBreakdown)
        ? item.costBreakdown
        : (isRecord(item.cost_breakdown) ? item.cost_breakdown : {});
    return ['materials', 'labour', 'equipment', 'subcontractors', 'otherCosts', 'other']
        .flatMap(key => Array.isArray(breakdown[key]) ? breakdown[key].filter(isRecord) : []);
}

function finiteCommercialNumber(value: unknown): number | null {
    const parsed = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(parsed)) return null;
    return Math.round((parsed + Number.EPSILON) * 10_000) / 10_000;
}

function commercialRateExGst(value: unknown, taxBasis: unknown): number | null {
    const parsed = finiteCommercialNumber(value);
    if (parsed === null) return null;
    // Current Pricing writes an explicit tax basis. Rows without one predate
    // that contract and were stored as GST-inclusive values.
    const basis = String(taxBasis || '').trim().toLowerCase();
    return finiteCommercialNumber(basis === 'ex_gst' ? parsed : parsed / 1.1);
}

function currentPricingRateExGst(row: UnknownRecord, field: 'rate' | 'costRate'): number | null {
    const basis = field === 'costRate'
        ? (row.costRateTaxBasis ?? row.cost_rate_tax_basis)
        : (row.chargeOutTaxBasis ?? row.charge_out_tax_basis);
    if (field === 'costRate' && !String(basis || '').trim()) {
        // Legacy sell rows were commonly GST-inclusive, but the paired
        // cost-to-business column was historically entered ex GST.
        return finiteCommercialNumber(row[field]);
    }
    return commercialRateExGst(row[field], basis);
}

function currentPricingCostRateExGst(row: UnknownRecord, line: UnknownRecord): number | null {
    const raw = finiteCommercialNumber(row.costRate);
    if (raw === null) return null;
    const basis = String(row.costRateTaxBasis ?? row.cost_rate_tax_basis ?? '').trim();
    if (basis) return commercialRateExGst(raw, basis);

    const sourceCost = finiteCommercialNumber(line.costRate ?? line.cost_rate);
    const sourceBasis = line.costRateTaxBasis ?? line.cost_rate_tax_basis;
    const sourceExGst = String(sourceBasis || '').trim()
        ? commercialRateExGst(sourceCost, sourceBasis)
        : sourceCost;
    const legacyIncGst = finiteCommercialNumber(raw / 1.1);
    // Internal Pricing normalises an old package to ex GST when the builder
    // selects it. Recognise that exact conversion without reclassifying every
    // historical blank-basis cost column as GST-inclusive.
    if (sourceExGst !== null && legacyIncGst !== null
        && Math.abs(sourceExGst - legacyIncGst) <= 0.005) {
        return legacyIncGst;
    }
    return raw;
}

function lineCommercialValueExGst(line: UnknownRecord, value: unknown): number | null {
    const basis = line.rateTaxBasis
        ?? line.rate_tax_basis
        ?? (line.rateIsFinalIncGst === true || line.rate_is_final_inc_gst === true ? 'inc_gst' : null);
    return commercialRateExGst(value, basis);
}

function normalisedUnit(value: unknown): string {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function lineQuantity(line: UnknownRecord, category: string): number {
    return finiteCommercialNumber(priceWorkingBillableQuantity(line, category)) ?? 0;
}

function lineRateSource(line: UnknownRecord): string {
    return String(line._rateSource ?? line._rate_source ?? '').trim().toLowerCase();
}

function linePricingItemId(line: UnknownRecord): string {
    return String(line.pricingItemId ?? line.pricing_item_id ?? '').trim();
}

function isFinalRatebookLine(line: UnknownRecord): boolean {
    const source = lineRateSource(line);
    if (source === 'market-grounded' || source === 'manual' || source === 'manual_override') return false;
    // Older saved scopes can retain final-rate flags after the catalogue
    // matcher has deliberately revoked canonical authority because a measured
    // input is missing. Pending review plus no canonical row ID must stay a
    // reviewable unknown, not become a false current-rate claim.
    if (pricingRowHasPendingResolution(line) && linePricingItemId(line).length === 0) return false;
    return line.rateIsFinalIncGst === true
        || line.rate_is_final_inc_gst === true
        || line.rateIsFinalChargeOut === true
        || line.rate_is_final_charge_out === true
        || source === 'business'
        // An explicit row link is a commercial authority claim even when a
        // legacy producer omitted the newer final-rate flags.
        || linePricingItemId(line).length > 0;
}

function lineSellRate(line: UnknownRecord, category: string): number | null {
    const field = String(priceWorkingRateField(line, category) || '');
    return finiteCommercialNumber(line[field] ?? line.cost);
}

function pricingRowName(row: UnknownRecord): string {
    return String(row.name || row.item || row.trade || '');
}

function commercialPricingSnapshot(
    businessPricing: unknown,
    trustedSnapshot: JsonObject,
): JsonObject {
    const source = isRecord(businessPricing) ? businessPricing : {};
    const trustedRows: UnknownRecord[] = Array.isArray(trustedSnapshot.rows)
        ? trustedSnapshot.rows.filter(isRecord) as UnknownRecord[]
        : [];
    const rows = trustedRows.map((trusted): JsonObject => {
        const category = String(trusted.cat || '');
        const sourceRows: UnknownRecord[] = Array.isArray(source[category])
            ? (source[category] as unknown[]).filter(isRecord) as UnknownRecord[]
            : [];
        const sourceRow = sourceRows.find(row => String(row.id || '') === String(trusted.id || ''))
            || sourceRows.find(row => normaliseKey(pricingRowName(row)) === normaliseKey(String(trusted.name || '')))
            || {};
        const costRate = finiteCommercialNumber(sourceRow.costRate ?? sourceRow.cost_rate);
        return {
            ...portableObject(trusted, '$currentPricing.trustedRow'),
            // Null means cost-to-business coverage is unknown. Never turn an
            // absent cost rate into zero: that would inflate reported margin.
            costRate: costRate !== null && costRate > 0 ? costRate : null,
        };
    });
    return {
        ...portableObject(trustedSnapshot, '$currentPricing.trustedSnapshot'),
        rows,
    };
}

function markLineUnresolved(line: UnknownRecord, reason: string): JsonObject {
    return {
        ...portableObject(line, '$quote.scope.costBreakdown.line'),
        pricingStatus: 'unresolved',
        unresolvedReason: reason,
    };
}

function currentPricingRowForCostLine(
    rowsByKey: Map<string, UnknownRecord>,
    category: (typeof COMMERCIAL_CATEGORY_KEYS)[number],
    ratebookCategory: string,
    pricingItemId: string,
): UnknownRecord | undefined {
    const exact = rowsByKey.get(`${ratebookCategory}\u0000${pricingItemId}`);
    if (exact) return exact;

    // Francis's approved price book stores some full-day trade charge-outs on
    // the Materials sheet. The canonical Stage 3 producer permits one narrow
    // cross-category link for those rows: a labour line may name the exact
    // Materials ID when that frozen row explicitly says Day Rate / Per day.
    // Keep this identical to pricing-match-applier; no other category or unit
    // may cross-certify from a reused identifier.
    //
    // The bundled book now files those trade rows under Labour with the SAME
    // ids, so the exact lookup above finds them there. This fallback stays
    // exactly as it was for rate books synced earlier and for saved quotes whose
    // labour line names the old row: every id that was valid stays valid.
    if (category !== 'labour') return undefined;
    const material = rowsByKey.get(`material_costs\u0000${pricingItemId}`);
    return material && isPerDayPricingUnit(material.unit, material.isDayRate === true)
        ? material
        : undefined;
}

/**
 * Bind every matched source line to one exact row in the frozen commercial
 * Pricing snapshot. The legacy global fingerprint alone is not row proof.
 */
export function reconcileCurrentCommercialPricing(
    scope: UnknownRecord[],
    pricingAuthority: CurrentPricingAuthorityContextV1,
): JsonObject[] {
    const snapshotRows: UnknownRecord[] = Array.isArray(pricingAuthority.ratesheetSnapshot.rows)
        ? pricingAuthority.ratesheetSnapshot.rows.filter(isRecord) as UnknownRecord[]
        : [];
    const rowsByKey = new Map(snapshotRows.map(row => [`${String(row.cat)}\u0000${String(row.id)}`, row]));
    return scope.map((rawItem, itemIndex) => {
        const item = portableObject(rawItem, `$quote.scope[${itemIndex}]`);
        const breakdownKey = isRecord(item.costBreakdown) ? 'costBreakdown'
            : (isRecord(item.cost_breakdown) ? 'cost_breakdown' : null);
        if (!breakdownKey) return item;
        const breakdown = portableObject(item[breakdownKey], `$quote.scope[${itemIndex}].${breakdownKey}`);

        for (const category of COMMERCIAL_CATEGORY_KEYS) {
            if (!Array.isArray(breakdown[category])) continue;
            const ratebookCategory = RATEBOOK_CATEGORY_BY_COST_CATEGORY[category];
            breakdown[category] = (breakdown[category] as JsonValue[]).map((rawLine, lineIndex) => {
                if (!isRecord(rawLine)) return rawLine;
                const line = portableObject(
                    rawLine,
                    `$quote.scope[${itemIndex}].${breakdownKey}.${category}[${lineIndex}]`,
                );
                const marketEvidence = validatedMarketRate(line);
                if (lineRateSource(line) === 'market-grounded') {
                    if (!marketEvidence) return markLineUnresolved(line, 'market_pricing_evidence_invalid');
                    const actualRate = lineSellRate(line, category);
                    const quantity = lineQuantity(line, category);
                    const actualTotal = finiteCommercialNumber(line.total ?? line.amount);
                    const expectedTotal = Math.round((marketEvidence.rate * quantity + Number.EPSILON) * 100) / 100;
                    if (actualRate === null
                        || Math.abs(actualRate - marketEvidence.rate) > 0.01
                        || actualTotal === null
                        || Math.abs(actualTotal - expectedTotal) > 0.01) {
                        return markLineUnresolved(line, 'market_pricing_line_mismatch');
                    }
                    return {
                        ...line,
                        marketPricingEvidence: marketEvidence.evidence,
                        authorityRef: `market-price:${hashCanonicalJson(marketEvidence.evidence)}`,
                        costRate: marketEvidence.costRate,
                    };
                }
                const claimsRatebook = isFinalRatebookLine(line);
                if (!claimsRatebook) return line;
                const pricingItemId = linePricingItemId(line);
                // Category is part of the authority key. A material line must
                // never certify from an equipment row merely because legacy
                // tenants reused the same identifier.
                const current = currentPricingRowForCostLine(
                    rowsByKey,
                    category,
                    ratebookCategory,
                    pricingItemId,
                );
                if (!pricingItemId || !current) {
                    return markLineUnresolved(line, 'current_pricing_row_missing');
                }

                const expectedRate = currentPricingRateExGst(current, 'rate');
                const actualRate = lineCommercialValueExGst(line, lineSellRate(line, category));
                const matchedRate = lineCommercialValueExGst(
                    line,
                    line.matchedRate ?? line.matched_rate,
                );
                const currentUnit = String(current.unit || '');
                const linkedDayRate = current.isDayRate === true;
                if (!hasExplicitPriceWorkingDriver(
                    linkedDayRate ? { ...line, matchedIsDayRate: true } : line,
                    category,
                    currentUnit,
                )) {
                    return markLineUnresolved(line, 'current_pricing_driver_missing');
                }
                const quantity = linkedDayRate
                    ? lineQuantity({ ...line, matchedIsDayRate: true }, category)
                    : lineQuantity(line, category);
                const actualTotal = lineCommercialValueExGst(line, line.total ?? line.amount);
                const expectedTotal = expectedRate === null ? null
                    : Math.round((expectedRate * quantity + Number.EPSILON) * 100) / 100;
                const lineUnit = priceWorkingUnit(line);
                const unitMismatch = !normalisedUnit(lineUnit)
                    || !normalisedUnit(current.unit)
                    || normalisedUnit(lineUnit) !== normalisedUnit(current.unit);
                const rateMismatch = expectedRate === null
                    || actualRate === null
                    || Math.abs(actualRate - expectedRate) > 0.005
                    || (matchedRate !== null && Math.abs(matchedRate - expectedRate) > 0.005);
                const totalMismatch = expectedTotal === null
                    || actualTotal === null
                    || Math.abs(actualTotal - expectedTotal) > 0.01;
                if (unitMismatch || rateMismatch || totalMismatch) {
                    return markLineUnresolved(
                        line,
                        unitMismatch ? 'current_pricing_unit_mismatch'
                            : rateMismatch ? 'current_pricing_rate_mismatch'
                                : 'current_pricing_total_mismatch',
                    );
                }

                const costRate = currentPricingCostRateExGst(current, line);
                const reconciled: JsonObject = {
                    ...line,
                    currentPricingRowHash: hashCanonicalJson(current),
                    currentPricingSnapshotHash: pricingAuthority.ratesheetSnapshotHash,
                };
                if (costRate !== null && costRate > 0) {
                    reconciled.costRate = costRate;
                    reconciled.costRateTaxBasis = 'ex_gst';
                }
                else delete reconciled.costRate;
                delete reconciled.cost_rate;
                delete reconciled.cost_rate_tax_basis;
                return reconciled;
            });
        }
        return { ...item, [breakdownKey]: breakdown };
    });
}

const CURRENT_PRICING_VALIDATION_REASONS = new Set([
    'market_pricing_evidence_invalid',
    'market_pricing_line_mismatch',
    'current_pricing_row_missing',
    'current_pricing_unit_mismatch',
    'current_pricing_rate_mismatch',
    'current_pricing_total_mismatch',
    'current_pricing_driver_missing',
]);

export interface CurrentCommercialPricingValidationIssueV1 extends JsonObject {
    itemIndex: number;
    category: string;
    lineIndex: number;
    reason: string;
}

/**
 * Validate constituent scope cost lines against one strict current Pricing
 * snapshot without mutating the supplied scope. Costing generation uses this
 * before it stamps that snapshot's fingerprint: a stale rate/unit/total cannot
 * become "fresh" merely because the project grand total happens to be equal.
 * Builder-manual rows remain builder-manual; valid market evidence is checked
 * but never rewritten into a rate-book claim.
 */
export function validateScopeAgainstCurrentCommercialPricingV1(
    scope: unknown,
    businessPricing: unknown,
): { valid: boolean; issues: CurrentCommercialPricingValidationIssueV1[] } {
    const portableScope = Array.isArray(scope)
        ? scope.map((item, index) => portableObject(item, `$scope[${index}]`))
        : [];
    const authority = buildCurrentPricingAuthorityContextV1(businessPricing);
    const reconciled = reconcileCurrentCommercialPricing(portableScope, authority);
    const issues: CurrentCommercialPricingValidationIssueV1[] = [];
    reconciled.forEach((item, itemIndex) => {
        const breakdown = isRecord(item.costBreakdown)
            ? item.costBreakdown
            : (isRecord(item.cost_breakdown) ? item.cost_breakdown : {});
        COMMERCIAL_CATEGORY_KEYS.forEach((category) => {
            const rows = Array.isArray(breakdown[category]) ? breakdown[category] : [];
            rows.forEach((rawLine, lineIndex) => {
                if (!isRecord(rawLine)) return;
                const reason = String(rawLine.unresolvedReason || '');
                if (!CURRENT_PRICING_VALIDATION_REASONS.has(reason)) return;
                issues.push({ itemIndex, category, lineIndex, reason });
            });
        });
    });
    return { valid: issues.length === 0, issues };
}

function actualRatebookCost(scopeItem: UnknownRecord): {
    knownCents: number;
    finalLineCount: number;
    coveredFinalLineCount: number;
    missingPaths: string[];
} {
    const breakdown = isRecord(scopeItem.costBreakdown)
        ? scopeItem.costBreakdown
        : (isRecord(scopeItem.cost_breakdown) ? scopeItem.cost_breakdown : {});
    let knownCents = 0;
    let finalLineCount = 0;
    let coveredFinalLineCount = 0;
    const missingPaths: string[] = [];
    for (const category of COMMERCIAL_CATEGORY_KEYS) {
        const rows = Array.isArray(breakdown[category]) ? breakdown[category].filter(isRecord) : [];
        rows.forEach((line, index) => {
            const marketEvidence = validatedMarketRate(line);
            if (marketEvidence) {
                finalLineCount += 1;
                const costRate = finiteCommercialNumber(line.costRate ?? line.cost_rate);
                if (costRate === null || costRate <= 0) {
                    missingPaths.push(`${category}[${index}]`);
                    return;
                }
                coveredFinalLineCount += 1;
                knownCents += Math.round(costRate * lineQuantity(line, category) * 100);
                return;
            }
            if (!isFinalRatebookLine(line)) return;
            finalLineCount += 1;
            const costRate = finiteCommercialNumber(line.costRate ?? line.cost_rate);
            if (costRate === null || costRate <= 0) {
                missingPaths.push(`${category}[${index}]`);
                return;
            }
            coveredFinalLineCount += 1;
            knownCents += Math.round(costRate * lineQuantity(line, category) * 100);
        });
    }
    return { knownCents, finalLineCount, coveredFinalLineCount, missingPaths };
}

function mapScopeToArtifactCostInputs(scopeItems: JsonObject[], options: UnknownRecord): any[] {
    const mapped = mapScopeToInputs(scopeItems, options);
    return mapped.map((input, index) => {
        const scopeItem = scopeItems[index] || {};
        const coverage = actualRatebookCost(scopeItem);
        const breakdown = isRecord(scopeItem.costBreakdown)
            ? scopeItem.costBreakdown
            : (isRecord(scopeItem.cost_breakdown) ? scopeItem.cost_breakdown : {});
        const complianceComponents: UnknownRecord[] = Array.isArray(breakdown.complianceComponents)
            ? breakdown.complianceComponents.filter(isRecord) as UnknownRecord[]
            : [];
        // Francis's regulated-work methodology deliberately presents SRD/DCD,
        // DBPA, contract, HBCF application and premium as one editable client
        // Preliminaries row. INV5 normally recognises only a standalone row by
        // title/id. Carry a cost-math-only presence label when (and only when)
        // the deterministic managed compliance receipt proves that aggregate
        // contains HBCF. This does not change the quote row or its amount.
        const managedComplianceContainsHbcf = (
            String(breakdown._costSource || breakdown.costSource || '') === 'francis-compliance-reconcile'
            && typeof breakdown.pricingMethodologyId === 'string'
            && breakdown.pricingMethodologyId.length > 0
            && complianceComponents.some(component => /^hbcf_/i.test(String(component.kind || '')))
        );
        const operatorIncludedHbcf = options.hbcfIncludedInScope === true && index === 0;
        return {
            ...input,
            ...((managedComplianceContainsHbcf || operatorIncludedHbcf)
                ? {
                    // cost-math currently carries `id` (but not title) into
                    // totals before INV5 runs. This identifier is adapter-local;
                    // the package ledger/client row retain the planned scope id.
                    id: `${String(input.id || scopeItem.id || index + 1)}-hbcf-included`,
                    title: `${String(input.title || scopeItem.title || 'Preliminaries')} (HBCF included)`,
                }
                : {}),
            _artifactActualRatebookCost: coverage.knownCents / 100,
            _artifactCostCoverage: coverage,
        };
    });
}

function calculateArtifactProjectTotals(inputs: any[], config: any): any {
    const totals = calculateProjectTotals(inputs, config);
    const items = totals.items.map((item: UnknownRecord, index: number) => {
        const actualRatebookCost = finiteCommercialNumber(inputs[index]?._artifactActualRatebookCost) ?? 0;
        if (actualRatebookCost <= 0) return item;
        const nextDirect = (finiteCommercialNumber(item.direct) ?? 0) + actualRatebookCost;
        const nextTotalCost = (finiteCommercialNumber(item.totalCost) ?? 0) + actualRatebookCost;
        const nextMargin = (finiteCommercialNumber(item.marginAmount) ?? 0) - actualRatebookCost;
        return {
            ...item,
            direct: nextDirect,
            totalCost: nextTotalCost,
            marginAmount: nextMargin,
            marginPct: nextTotalCost > 0
                ? Math.round((nextMargin / nextTotalCost) * 10_000) / 100
                : 0,
            trace: {
                ...(isRecord(item.trace) ? item.trace : {}),
                direct: nextDirect,
                margin: nextMargin,
            },
        };
    });
    const ratebookCost = inputs.reduce((sum, input) => (
        sum + (finiteCommercialNumber(input?._artifactActualRatebookCost) ?? 0)
    ), 0);
    const totalsTrace = isRecord(totals.trace) ? totals.trace : {};
    return {
        ...totals,
        items,
        trace: {
            ...totalsTrace,
            direct: (finiteCommercialNumber(totalsTrace.direct) ?? 0) + ratebookCost,
            margin: (finiteCommercialNumber(totalsTrace.margin) ?? 0) - ratebookCost,
        },
    };
}

function unresolvedItemIds(scope: UnknownRecord[], expectedFingerprint: string): string[] {
    return scope.flatMap((item, index) => {
        const id = String(item.id || item.code || index + 1);
        if (item.amountLocked === true || item.amount_locked === true) return [];
        const breakdown = isRecord(item.costBreakdown)
            ? item.costBreakdown
            : (isRecord(item.cost_breakdown) ? item.cost_breakdown : {});
        const lines = sourceLines(item);
        const fingerprint = String(breakdown.pricingFingerprint || breakdown.pricing_fingerprint || '');
        const unresolved = scopeItemHasUnresolvedPricing(item)
            || lines.length === 0
            || fingerprint !== expectedFingerprint
            || String(breakdown.pricingStatus || '').toLowerCase() === 'unresolved'
            || lines.some(line => {
                const source = String(line._rateSource || '').toLowerCase();
                return source === 'ai'
                    || source === 'ambiguous'
                    || source === 'unresolved'
                    || String(line.pricingStatus || '').toLowerCase() === 'unresolved'
                    || !!String(line.unresolvedReason || '').trim();
            });
        return unresolved ? [id] : [];
    });
}

function stampScopeAuthority(
    scope: UnknownRecord[],
    pricingAuthority: CurrentPricingAuthorityContextV1,
    sourceQuoteHash: Sha256Digest,
): { scope: JsonObject[]; authorityRefs: string[] } {
    const rateRef = pricingAuthority.authorityRefs[0];
    if (!rateRef) fail('QUOTE_ARTIFACT_PRICING_AUTHORITY_MISSING', 'Current Pricing has no authority reference.');
    const authorityRefs = new Set<string>(pricingAuthority.authorityRefs);
    const stamped = scope.map((raw, index) => {
        const source = portableObject(raw, `$quote.scope[${index}]`);
        sourceLines(source).forEach(line => {
            const ref = String(line.authorityRef || '').trim();
            if (ref.startsWith('market-price:')) authorityRefs.add(ref);
            if (ref.startsWith('pricing-methodology:')) authorityRefs.add(ref);
        });
        const id = String(source.id || source.code || index + 1);
        let authorityRef = rateRef;
        // Generated statutory rows carry amountLocked as part of their policy
        // receipt, so that broad flag alone must still resolve to statutory
        // authority. A price-specific builder receipt is different: Stage 4/5
        // records an explicit amount reason, timestamp or editor when the
        // builder overrides the visible statutory amount. That deliberate
        // commercial decision must outrank the generated policy receipt.
        if (hasReviewableManualAmountAuthority(source)) {
            authorityRef = `operator-lock:${sourceQuoteHash}:${id}`;
            authorityRefs.add(authorityRef);
        } else if (isStatutoryItem(raw)) {
            authorityRef = `statutory-policy:${pricingAuthority.ratesheetSnapshotHash}`;
            authorityRefs.add(authorityRef);
        } else if (source.amountLocked === true || source.amount_locked === true) {
            authorityRef = `operator-lock:${sourceQuoteHash}:${id}`;
            authorityRefs.add(authorityRef);
        }
        // The authority snapshot needs the exact commercial source row, not a
        // second copy of photos, prose, inclusions or the Work Method. Keeping
        // this projection narrow avoids multiplying media payloads inside the
        // builder-only costing artifact while retaining every cost-math input.
        const item: JsonObject = {
            id,
            title: String(source.title || `Scope item ${index + 1}`),
            amount: typeof source.amount === 'number' || typeof source.amount === 'string'
                ? source.amount
                : 0,
            authorityRef,
        };
        for (const key of [
            'amountLocked', 'amount_locked', 'amountLockReason', 'amount_lock_reason',
            'amountEditedAt', 'amount_edited_at', 'amountEditedBy', 'amount_edited_by',
            'priceEditedAt', 'price_edited_at', 'priceEditedBy', 'price_edited_by',
            'amountLockedAt', 'amount_locked_at', 'humanEditedAt', 'human_edited_at',
            'editedBy', 'edited_by',
            'pricingBasis', 'pricing_basis', 'isGstInclusive', 'gstExempt', 'gst_exempt',
            'marginPercent', 'marginPctOverride', 'provisionalSum',
            'excludeFromFixedAllocations', 'costBreakdown', 'cost_breakdown',
        ]) {
            const value = source[key];
            if (value !== undefined) item[key] = value;
        }
        return item;
    });
    return { scope: stamped, authorityRefs: [...authorityRefs] };
}

function stripQuoteArtifactTransportState(value: JsonValue, parentKey = ''): JsonValue {
    if (Array.isArray(value)) return value.map(entry => stripQuoteArtifactTransportState(entry, parentKey));
    if (!value || typeof value !== 'object') return value;
    const result: JsonObject = {};
    for (const [key, entry] of Object.entries(value)) {
        const normalised = normaliseKey(key);
        if (MEDIA_KEYS.has(normalised)
            || normalised === 'businesspricing'
            || normalised === 'productionartifactbinding'
            || (normaliseKey(parentKey) === 'meta' && [
                'completion', 'independentcritic', 'issuereadiness', 'rfqfit',
            ].includes(normalised))) continue;
        result[key] = stripQuoteArtifactTransportState(entry, key);
    }
    return result;
}

/** Semantic hash of Stage 5 content, excluding media transport and volatile review metadata. */
export function quoteArtifactSourceHashV1(quoteData: unknown): Sha256Digest {
    const quote = portableObject(quoteData, '$quote');
    return hashCanonicalJson(stripQuoteArtifactTransportState(quote));
}

export interface ProductionArtifactsCurrentResultV1 {
    current: boolean;
    reasons: string[];
}

/**
 * Pure stale-binding verifier for save/finalise Adapters. This deliberately
 * performs no write: the existing Stage 5 save/PDF boundary owns whether to
 * rebuild or clear stale artifacts.
 */
export function verifyProductionQuoteArtifactsCurrentV1(
    quoteData: unknown,
    artifactsValue: unknown,
    projectWMS?: unknown,
): ProductionArtifactsCurrentResultV1 {
    const reasons: string[] = [];
    if (!isRecord(artifactsValue)) return { current: false, reasons: ['artifacts_missing'] };
    const artifacts = artifactsValue as unknown as ProductionQuoteArtifactsV1;
    const binding = isRecord(artifacts.quoteBinding) ? artifacts.quoteBinding : {};
    if (binding.sourceQuoteHash !== quoteArtifactSourceHashV1(quoteData)) reasons.push('quote_content_changed');

    for (const key of ['staffDiagnosticReport', 'staffMethodReport', 'staffCosting', 'clientQuote'] as const) {
        const artifact = artifacts[key];
        if (!artifact || hashCanonicalJson(artifact.structured) !== artifact.hashes?.structuredHash) {
            reasons.push(`${key}_structured_hash_mismatch`);
        }
        if (!artifact || sha256Text(artifact.text) !== artifact.hashes?.textHash) {
            reasons.push(`${key}_text_hash_mismatch`);
        }
    }
    const withoutIntegrity = {
        schemaVersion: artifacts.schemaVersion,
        kind: artifacts.kind,
        generatedAt: artifacts.generatedAt,
        projectId: artifacts.projectId,
        quoteBinding: artifacts.quoteBinding,
        staffDiagnosticReport: artifacts.staffDiagnosticReport,
        staffMethodReport: artifacts.staffMethodReport,
        staffCosting: artifacts.staffCosting,
        clientQuote: artifacts.clientQuote,
    } as Omit<ProductionQuoteArtifactsV1, 'integrity'>;
    if (hashCanonicalJson(artifactContentPayload(withoutIntegrity)) !== artifacts.integrity?.contentSetHash) {
        reasons.push('content_set_hash_mismatch');
    }
    if (binding.methodSource === 'project-wms') {
        const wms = isRecord(projectWMS) ? projectWMS : {};
        const structured = isRecord(wms.structured) ? wms.structured : wms;
        if (hashCanonicalJson(projectMethodDocument(structured)) !== binding.methodSourceHash) {
            reasons.push('work_method_changed');
        }
    }
    return { current: reasons.length === 0, reasons };
}

function artifactContentPayload(artifacts: Omit<ProductionQuoteArtifactsV1, 'integrity'>): JsonObject {
    return {
        sourceQuoteHash: artifacts.quoteBinding.sourceQuoteHash as JsonValue,
        pricingAuthoritySnapshotHash: artifacts.quoteBinding.pricingAuthoritySnapshotHash as JsonValue,
        pricingLedgerHash: artifacts.quoteBinding.pricingLedgerHash as JsonValue,
        staffDiagnostic: cloneCanonicalJson(artifacts.staffDiagnosticReport.hashes) as unknown as JsonObject,
        staffMethod: cloneCanonicalJson(artifacts.staffMethodReport.hashes) as unknown as JsonObject,
        staffCosting: cloneCanonicalJson(artifacts.staffCosting.hashes) as unknown as JsonObject,
        clientQuote: cloneCanonicalJson(artifacts.clientQuote.hashes) as unknown as JsonObject,
    };
}

function enrichActualCostCoverage(
    costing: StaffCostingStructuredV1,
    scope: JsonObject[],
): StaffCostingStructuredV1 {
    const scopeById = new Map(scope.map((item, index) => [String(item.id || index + 1), item]));
    const rows = costing.rows.map(row => {
        const source = scopeById.get(row.scopeItemId) || {};
        const coverage = actualRatebookCost(source);
        const breakdown = isRecord(source.costBreakdown)
            ? source.costBreakdown
            : (isRecord(source.cost_breakdown) ? source.cost_breakdown : {});
        const hasExplicitDirect = (finiteCommercialNumber(
            breakdown.directCostTotal ?? breakdown.direct_cost_total,
        ) ?? 0) > 0;
        const hasNonFinalSource = sourceLines(source).some(line => !isFinalRatebookLine(line));
        const isManagedComplianceSellRow = row.scopeItemId === 'francis-preliminaries-administration'
            || String(breakdown._costSource || breakdown.costSource || '') === 'francis-compliance-reconcile';
        const managedComplianceCostUnknown = isManagedComplianceSellRow
            && coverage.coveredFinalLineCount === 0;
        const missingAllCostEvidence = coverage.finalLineCount === 0
            && !hasExplicitDirect
            && !hasNonFinalSource
            && (source.amountLocked === true || source.amount_locked === true);
        const status = managedComplianceCostUnknown || missingAllCostEvidence || (
            coverage.finalLineCount > 0 && coverage.coveredFinalLineCount === 0
        )
            ? 'unknown'
            : coverage.coveredFinalLineCount < coverage.finalLineCount
                ? 'partial'
                : 'complete';
        const safeRow = managedComplianceCostUnknown
            ? {
                ...row,
                directCents: 0,
                contingencyCents: 0,
                overheadCents: 0,
                marginCents: 0,
            }
            : row;
        return {
            ...safeRow,
            actualCostCoverage: {
                status,
                knownDirectCostCents: managedComplianceCostUnknown ? 0 : row.directCents,
                finalRatebookLineCount: coverage.finalLineCount,
                coveredFinalRatebookLineCount: coverage.coveredFinalLineCount,
                missingCostRatePaths: coverage.missingPaths,
            },
        };
    });
    const statuses = rows.map(row => String((row.actualCostCoverage as JsonObject)?.status || 'unknown'));
    const status = statuses.every(value => value === 'complete')
        ? 'complete'
        : statuses.every(value => value === 'unknown')
            ? 'unknown'
            : 'partial';
    const pricingMethodology = scope.flatMap((item, index): JsonObject[] => {
        const breakdown = isRecord(item.costBreakdown)
            ? item.costBreakdown
            : (isRecord(item.cost_breakdown) ? item.cost_breakdown : {});
        const methodologyId = String(breakdown.pricingMethodologyId || '').trim();
        if (!methodologyId) return [];
        const cents = (value: unknown): number | null => {
            const number = finiteCommercialNumber(value);
            return number === null ? null : Math.round(number * 100);
        };
        return [{
            scopeItemId: String(item.id || item.code || index + 1),
            methodologyId,
            pricingBand: String(breakdown.pricingBand || '').trim(),
            pricingBandMultiplier: finiteCommercialNumber(breakdown.pricingBandMultiplier),
            rateCardSellExGstCents: cents(breakdown.rateCardSellExGst),
            administrationShareExGstCents: cents(breakdown.administrationShareExGst),
            unroundedIncGstCents: cents(breakdown.pricingUnroundedIncGst),
            roundingDeltaIncGstCents: cents(breakdown.pricingRoundingDeltaIncGst),
            finalIncGstCents: cents(item.amount),
        }];
    });
    return {
        ...costing,
        rows,
        ...(pricingMethodology.length > 0 ? { pricingMethodology } : {}),
        actualCostCoverage: {
            status,
            completeScopeItemCount: statuses.filter(value => value === 'complete').length,
            totalScopeItemCount: statuses.length,
        },
    };
}

function buildHbcfWaiverReceipt(
    formData: unknown,
    projectId: string | null | undefined,
    sourceQuoteHash: Sha256Digest,
    generatedAt: string,
): ({ authorityRef: string; reason: string; confirmedBy: string; confirmedAt: string } & JsonObject) | undefined {
    const form = isRecord(formData) ? formData : {};
    if (form.hbcfWaived !== true || form.hbcfIncludedInScope === true) return undefined;
    const reason = String(form.hbcfWaiverReason || '').trim()
        || 'Persisted project setting hbcfWaived=true; the legacy record did not capture a separate exemption reason.';
    const confirmedBy = String(form.hbcfWaiverConfirmedBy || '').trim()
        || 'Builder via persisted QuoteMaker project setting';
    const explicitConfirmedAt = String(form.hbcfWaiverConfirmedAt || '').trim();
    const confirmedAt = explicitConfirmedAt && Number.isFinite(Date.parse(explicitConfirmedAt))
        ? explicitConfirmedAt
        : generatedAt;
    const decisionHash = hashCanonicalJson({
        projectId: projectId || null,
        hbcfWaived: true,
        hbcfIncludedInScope: false,
        reason,
        confirmedBy,
        confirmedAt,
        sourceQuoteHash,
    });
    return {
        authorityRef: `operator-waiver:hbcf:${decisionHash}`,
        reason,
        confirmedBy,
        confirmedAt,
        receiptCompleteness: explicitConfirmedAt && form.hbcfWaiverReason && form.hbcfWaiverConfirmedBy
            ? 'complete'
            : 'legacy-persisted-decision',
    };
}

/** Freeze the trusted current-Pricing projection used by both Stage 5 and its staff costing artifact. */
export function buildCurrentPricingAuthorityContextV1(
    businessPricing: unknown,
): CurrentPricingAuthorityContextV1 {
    const pricingInput = businessPricing as Record<string, unknown> | null;
    const trustedSnapshot = canonicalRatesheetAuthority(pricingInput);
    const fingerprint = ratesheetFingerprint(pricingInput);
    if (!trustedSnapshot || !fingerprint) {
        fail(
            'QUOTE_ARTIFACT_PRICING_AUTHORITY_MISSING',
            'Stage 5 requires a readable current Pricing snapshot before quote generation.',
        );
    }
    // The legacy fingerprint remains the app-wide charge-rate drift sentinel.
    // This adjacent cryptographic snapshot additionally binds costRate because
    // staff actual-cost/margin reporting consumes it as commercial authority.
    const trustedAuthority = portableObject(trustedSnapshot, '$currentPricing.trustedSnapshot');
    const snapshot = commercialPricingSnapshot(pricingInput, trustedAuthority);
    const snapshotHash = hashCanonicalJson(snapshot);
    const authorityRef = `quotemaker-ratesheet:${snapshotHash}`;
    return cloneCanonicalJson({
        schemaVersion: 1,
        authorityId: AUTHORITY_ID,
        authorityRefs: [authorityRef],
        ratesheetFingerprint: fingerprint,
        ratesheetSnapshot: snapshot,
        ratesheetSnapshotHash: snapshotHash,
    }) as unknown as CurrentPricingAuthorityContextV1;
}

/**
 * Production Adapter for the four Stage 5 supporting artifacts. This is not a
 * finalised Quote Artifact Package: finalisation still requires the existing
 * operator-review, PDF and receipt-chain boundary.
 */
/**
 * The pricing step of the production artifacts: the quote's items priced on
 * the frozen current Pricing, through the cost-math authority and its
 * invariants (INV5 among them). It throws the authority's QuoteContractError
 * when the items are refused. The artifacts builder below runs it, and the AI
 * quote writer runs it on its start rows before the paid author call, so a
 * refusal decided by the Stage 4 items alone costs nothing (review R10,
 * 2026-10-23).
 */
async function priceQuoteForArtifactsV1(
    input: Pick<BuildProductionQuoteArtifactsInputV1, 'projectId' | 'formData' | 'pricingAuthority' | 'projectDefaultMargin' | 'signal'>,
    quote: JsonObject,
    quoteScope: JsonObject[],
    sourceQuoteHash: Sha256Digest,
) {
    const pricingAuthority = cloneCanonicalJson(input.pricingAuthority);
    if (hashCanonicalJson(pricingAuthority.ratesheetSnapshot) !== pricingAuthority.ratesheetSnapshotHash) {
        fail('QUOTE_ARTIFACT_PRICING_AUTHORITY_DRIFT', 'Current Pricing snapshot hash does not match its payload.');
    }
    const currentCommercialScope = reconcileCurrentCommercialPricing(quoteScope, pricingAuthority);
    const stamped = stampScopeAuthority(currentCommercialScope, pricingAuthority, sourceQuoteHash);
    const unresolvedItems = unresolvedItemIds(currentCommercialScope, pricingAuthority.ratesheetFingerprint);
    const generatedAt = String((isRecord(quote.meta) && quote.meta.generatedAt) || new Date().toISOString());
    const hbcfWaiver = buildHbcfWaiverReceipt(
        input.formData,
        input.projectId,
        sourceQuoteHash,
        generatedAt,
    );
    if (hbcfWaiver) stamped.authorityRefs.push(hbcfWaiver.authorityRef);
    const form = isRecord(input.formData) ? input.formData : {};
    const hbcfIncludedInScope = form.hbcfWaived !== true && form.hbcfIncludedInScope === true;
    if (hbcfIncludedInScope) {
        stamped.authorityRefs.push(`operator-inclusion:hbcf:${sourceQuoteHash}`);
    }
    const costingConfig = isRecord(pricingAuthority.ratesheetSnapshot.costingConfig)
        ? pricingAuthority.ratesheetSnapshot.costingConfig as JsonObject
        : {};
    const pricing = createQuoteMakerCostMathPricingAuthorityV1({
        authorityId: pricingAuthority.authorityId,
        costMath: {
            mapScopeToInputs: (scopeItems, options) => mapScopeToArtifactCostInputs(scopeItems, {
                ...options,
                hbcfIncludedInScope,
            }),
            buildCostConfig,
            calculateProjectTotals: calculateArtifactProjectTotals,
        },
        useVisibleAmountOverride: true,
        async resolveScope() {
            return {
                scopeItems: stamped.scope,
                authorityRefs: stamped.authorityRefs,
                unresolvedItems,
                costingConfig,
                defaultMarginPct: Number.isFinite(input.projectDefaultMargin)
                    ? Number(input.projectDefaultMargin)
                    : undefined,
                inputAmountsAreGstInclusive: quote.isGstInclusive !== false,
                rateFinalNoMarkup: true,
                statutorySettings: {
                    currency: 'AUD',
                    gstRate: 0.1,
                    ratesheetFingerprint: pricingAuthority.ratesheetFingerprint,
                    ratesheetSnapshotHash: pricingAuthority.ratesheetSnapshotHash,
                    hbcfIncludedInScope,
                },
                ...(hbcfWaiver ? { hbcfWaiver } : {}),
            };
        },
    });
    const signal = input.signal || new AbortController().signal;
    const pricingOutput = await pricing.resolvePricing({
        runId: `stage5-artifacts:${sourceQuoteHash.slice('sha256:'.length, 'sha256:'.length + 16)}`,
        projectId: input.projectId || 'local-unsaved-project',
        projectVersion: String((isRecord(quote.meta) && quote.meta.generatedAt) || 'stage5-current'),
        signal,
        state: {
            scopePlan: {
                items: stamped.scope.map((item, index) => ({
                    id: String(item.id || index + 1),
                    title: String(item.title || `Scope item ${index + 1}`),
                })),
            },
        },
        configuration: {
            ratesheetFingerprint: pricingAuthority.ratesheetFingerprint,
            ratesheetSnapshotHash: pricingAuthority.ratesheetSnapshotHash,
        },
        project: {},
    });
    return { pricingOutput, stamped, pricingAuthority, generatedAt };
}

/**
 * Prices a provisional quote holding these items exactly as the artifacts
 * builder will, and throws the same refusal. Nothing is built or stored.
 */
export async function assertProductionPricingAcceptsQuoteV1(
    input: Pick<BuildProductionQuoteArtifactsInputV1, 'projectId' | 'quoteData' | 'formData' | 'pricingAuthority' | 'projectDefaultMargin' | 'signal'>,
): Promise<void> {
    const quote = portableObject(input.quoteData, '$quote');
    const quoteScope = Array.isArray(quote.scope)
        ? quote.scope.map((item, index) => portableObject(item, `$quote.scope[${index}]`))
        : [];
    if (quoteScope.length === 0) return;
    await priceQuoteForArtifactsV1(input, quote, quoteScope, quoteArtifactSourceHashV1(quote));
}

export async function buildProductionQuoteArtifactsV1(
    input: BuildProductionQuoteArtifactsInputV1,
): Promise<ProductionQuoteArtifactsV1> {
    const quote = portableObject(input.quoteData, '$quote');
    const quoteScope = Array.isArray(quote.scope)
        ? quote.scope.map((item, index) => portableObject(item, `$quote.scope[${index}]`))
        : [];
    if (quoteScope.length === 0) {
        fail('QUOTE_ARTIFACT_SCOPE_MISSING', 'Stage 5 cannot create artifacts for an empty quote scope.');
    }
    const sourceQuoteHash = quoteArtifactSourceHashV1(quote);
    const { pricingOutput, stamped, pricingAuthority, generatedAt } = await priceQuoteForArtifactsV1(input, quote, quoteScope, sourceQuoteHash);
    const costing = enrichActualCostCoverage(
        projectStaffCostingStructuredV1(pricingOutput as unknown as JsonObject),
        stamped.scope,
    );
    const workflowMode = String(input.workflowMode || 'full-ai-assisted');
    const diagnostic = diagnosticStructured(input.analysis, input.diagnosticResult, workflowMode, quoteScope);
    const methodResult = methodStructured(
        input.projectWMS,
        quoteScope,
        workflowMode,
        input.projectWMSIsCurrent !== false,
        quote.contractor,
    );
    const method = methodResult.structured;
    const client = projectClientQuoteStructuredV1(clientQuoteSource(quote, costing, method), costing);
    const staffLeak = findInternalStaffArtifactLeakPath(client, '$productionArtifacts.clientQuote');
    if (staffLeak) {
        fail('QUOTE_ARTIFACT_CLIENT_LEAK', `Client Quote contains an internal staff artifact at ${staffLeak}.`);
    }
    const staffDiagnosticReport = textArtifact(diagnostic, projectStaffDiagnosticTextV1(diagnostic));
    const staffMethodReport = textArtifact(method, projectStaffMethodTextV1(method));
    const staffCosting = textArtifact(costing, projectStaffCostingTextV1(costing));
    const clientQuote = textArtifact(client, projectClientQuoteTextV1(client));
    const withoutIntegrity: Omit<ProductionQuoteArtifactsV1, 'integrity'> = {
        schemaVersion: 1,
        kind: KIND,
        generatedAt,
        projectId: input.projectId || null,
        quoteBinding: {
            sourceQuoteHash,
            clientQuoteStructuredHash: clientQuote.hashes.structuredHash,
            pricingAuthoritySnapshotHash: costing.pricingAuthoritySnapshotHash,
            pricingLedgerHash: costing.pricingLedgerHash,
            ratesheetFingerprint: pricingAuthority.ratesheetFingerprint,
            ratesheetSnapshotHash: pricingAuthority.ratesheetSnapshotHash,
            methodSource: methodResult.source,
            methodSourceHash: methodResult.sourceHash,
            projectWMSIsCurrent: input.projectWMSIsCurrent !== false,
        },
        staffDiagnosticReport,
        staffMethodReport,
        staffCosting,
        clientQuote,
    };
    const contentSetHash = hashCanonicalJson(artifactContentPayload(withoutIntegrity));
    return cloneCanonicalJson({
        ...withoutIntegrity,
        integrity: { contentSetHash },
    }) as unknown as ProductionQuoteArtifactsV1;
}

/** Add only the content-addressed binding to quoteData; staff artifacts stay separate. */
export function bindProductionArtifactsToQuoteV1(
    quoteData: UnknownRecord,
    artifacts: ProductionQuoteArtifactsV1,
): UnknownRecord {
    const quote = portableObject(quoteData, '$quote');
    const meta = isRecord(quote.meta) ? quote.meta : {};
    return {
        ...quote,
        meta: {
            ...meta,
            productionArtifactBinding: {
                schemaVersion: 1,
                sourceQuoteHash: artifacts.quoteBinding.sourceQuoteHash,
                clientQuoteStructuredHash: artifacts.clientQuote.hashes.structuredHash,
                pricingAuthoritySnapshotHash: artifacts.staffCosting.structured.pricingAuthoritySnapshotHash,
                pricingLedgerHash: artifacts.staffCosting.structured.pricingLedgerHash,
                ratesheetFingerprint: artifacts.quoteBinding.ratesheetFingerprint,
                ratesheetSnapshotHash: artifacts.quoteBinding.ratesheetSnapshotHash,
                methodSource: artifacts.quoteBinding.methodSource,
                methodSourceHash: artifacts.quoteBinding.methodSourceHash,
                contentSetHash: artifacts.integrity.contentSetHash,
            },
        },
    };
}

/** Persistence seam: builder-only artifacts live in AI context, never client quote data. */
export function withQuoteArtifactsInAiContext(
    existingAiContext: unknown,
    quoteArtifacts: unknown,
): UnknownRecord {
    const existing = isRecord(existingAiContext) ? existingAiContext : {};
    return quoteArtifacts === undefined
        ? { ...existing }
        : { ...existing, quoteArtifacts: portableValue(quoteArtifacts, '$aiContext.quoteArtifacts') };
}
